/**
 * Motor social (B-37) — contas + publicação.
 * O segredo NUNCA volta do backend: só `has_credentials`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, API_BASE } from '@/services/api';

export interface SocialAccount {
  id: string;
  platform: string;  // vem do REGISTRY — nunca chumbar rede no front
  handle: string | null;
  name: string | null;
  active: boolean;
  has_credentials: boolean;
  created_at: string | null;
}

export interface PlatformField {
  key: string;
  label: string;
  type: 'text' | 'password';
  required: boolean;
  secret: boolean;
  hint: string;
  placeholder: string;
}

/** Vem do REGISTRY do backend — adicionar rede é dado, não código no front. */
export interface PlatformDef {
  key: string;
  label: string;
  max_chars: number;
  available: boolean;
  capabilities: string[];
  preferred_format: string;
  hint: string;
  doc_url: string;
  fields: PlatformField[];
}

export interface FormatDef {
  key: string;
  width: number;
  height: number;
  ratio: string;
}

export interface TemplateDef {
  id: string;
  name: string;
  brand_name: string;
  has_logo: boolean;
  accent_hex: string;
  text_hex: string;
  position: 'bottom' | 'top';
  scrim: boolean;
  headline_max_chars: number;
  is_default: boolean;
}

export interface SocialPostRow {
  id: string;
  platform: string;
  url: string | null;
  caption: string | null;
  site: string | null;
  wp_post_id: string | null;
  created_at: string | null;
}

export interface PublishResultRow {
  account_id: string;
  platform?: string;
  ok: boolean;
  url?: string | null;
  error?: string | null;
}

const ACCOUNTS_KEY = ['social', 'accounts'];

export function usePlatformDefs() {
  return useQuery({
    queryKey: ['social', 'platforms'],
    queryFn: () => api.get<{ platforms: PlatformDef[] }>('/social/platforms'),
    select: (d) => d.platforms,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useSocialAccounts() {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: () => api.get<SocialAccount[]>('/social/accounts'),
    retry: false,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      platform: string;
      handle: string;
      name?: string;
      credentials: Record<string, string>;
    }) => api.post<SocialAccount>('/social/accounts', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.patch<SocialAccount>(`/social/accounts/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/social/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useTestAccount() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; detail: string }>(`/social/accounts/${id}/test`, {}),
  });
}

export function usePublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      account_ids: string[];
      text: string;
      link?: string;
      image_urls?: string[];
      source_kind?: string;
      source_site?: string;
      source_wp_post_id?: string;
    }) =>
      api.post<{ results: PublishResultRow[]; ok: number; total: number }>(
        '/social/publish',
        payload,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'posts'] }),
  });
}

export function useSocialPosts() {
  return useQuery({
    queryKey: ['social', 'posts'],
    queryFn: () => api.get<SocialPostRow[]>('/social/posts'),
    retry: false,
  });
}

// ─── Arte (B-38): formatos, template da marca e preview ──────────────────────

export function useFormats() {
  return useQuery({
    queryKey: ['social', 'formats'],
    queryFn: () => api.get<{ formats: FormatDef[]; default: string }>('/social/formats'),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useTemplate() {
  return useQuery({
    queryKey: ['social', 'template'],
    queryFn: () =>
      api.get<{ template: TemplateDef | null; headline_budget: number }>('/social/template'),
    retry: false,
  });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<TemplateDef> & { logo_b64?: string | null }) =>
      api.put<TemplateDef>('/social/template', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'template'] }),
  });
}

/** Preview devolve PNG binário — fora do api.ts (que assume JSON). */
export async function fetchPreview(
  body: { headline_overlay: string; image_url: string; fmt: string },
  token: string | null,
): Promise<{ blobUrl: string; used: string; truncated: boolean }> {
  // fetch cru (o retorno é um blob de imagem), mas a base tem de ser a mesma
  // do cliente da casa — caminho relativo quebrava toda instalação com a API
  // em origem separada (o 404 vinha do servidor do FRONT, não da API).
  const r = await fetch(`${API_BASE}/social/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const detail = await r.json().catch(() => null);
    throw new Error((detail as { detail?: string } | null)?.detail || `Erro ${r.status}`);
  }
  const blob = await r.blob();
  return {
    blobUrl: URL.createObjectURL(blob),
    used: r.headers.get('X-Overlay-Used') || '',
    truncated: r.headers.get('X-Overlay-Truncated') === '1',
  };
}

export function usePublishArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      account_ids: string[];
      headline_overlay: string;
      caption: string;
      image_url?: string;
      link?: string;
      fmt?: string;
    }) =>
      api.post<{ results: PublishResultRow[]; ok: number; total: number; asset_url: string | null; media: { rendered: boolean } }>(
        '/social/publish-article',
        payload,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'posts'] }),
  });
}
